import type { Store } from '../types/index.js';
import type { CartRoverConfig, CartRoverCredentials } from './cartrover/config.js';
import type { ShipStationConfig, ShipStationCredentials } from './shipstation/config.js';

/** Ops integration capability slots (per store). */
export type IntegrationCapability =
  | 'orderExport'
  | 'inventory'
  | 'fulfillment'
  | 'catalogIngest';

/** Provider identifiers for capability slots. */
export type IntegrationProviderId = 'native' | 'none' | 'shipstation' | 'cartrover';

/** Per-store capability → provider assignment map. */
export type IntegrationProviders = Record<IntegrationCapability, IntegrationProviderId>;

/**
 * Compile-time integration module contract (Option B — no runtime registry).
 * Implementors document shape; handlers wire directly in the API router.
 */
export interface StoreIntegrationModule {
  id: IntegrationProviderId;
  label: string;
  capabilities: IntegrationCapability[];
}

export const DEFAULT_INTEGRATION_PROVIDERS: IntegrationProviders = {
  orderExport: 'native',
  inventory: 'native',
  fulfillment: 'native',
  catalogIngest: 'none',
};

const VALID_PROVIDER_IDS: ReadonlySet<string> = new Set([
  'native',
  'none',
  'shipstation',
  'cartrover',
]);

function isIntegrationProviderId(value: string): value is IntegrationProviderId {
  return VALID_PROVIDER_IDS.has(value);
}

/**
 * Merges stored slot assignments with defaults; unknown values fall back per capability.
 */
export function mergeIntegrationProviders(
  partial?: Partial<IntegrationProviders>
): IntegrationProviders {
  const merged: IntegrationProviders = { ...DEFAULT_INTEGRATION_PROVIDERS };
  if (!partial) {
    return merged;
  }
  for (const capability of Object.keys(DEFAULT_INTEGRATION_PROVIDERS) as IntegrationCapability[]) {
    const candidate = partial[capability];
    if (candidate !== undefined && isIntegrationProviderId(candidate)) {
      merged[capability] = candidate;
    }
  }
  return merged;
}

/**
 * Reads the active provider for a capability from store settings.
 */
export function resolveProvider(
  store: Store,
  capability: IntegrationCapability
): IntegrationProviderId {
  const providers = mergeIntegrationProviders(store.settings.integrationProviders);
  return providers[capability];
}

/**
 * Returns true when the store has assigned the given provider to the capability slot.
 */
export function isProviderActive(
  store: Store,
  capability: IntegrationCapability,
  providerId: IntegrationProviderId
): boolean {
  return resolveProvider(store, capability) === providerId;
}

/** Integration module row returned by GET /integrations. */
export interface IntegrationModuleStatus {
  id: string;
  label: string;
  capabilities: IntegrationCapability[];
  enabled: boolean;
  configured: boolean;
}

/** Store integrations hub list payload. */
export interface IntegrationsListResponse {
  providers: IntegrationProviders;
  modules: IntegrationModuleStatus[];
}

/** GET /integrations/shipstation — redacted credentials on read. */
export interface ShipStationIntegrationResponse {
  id: string;
  storeId: string;
  provider: 'shipstation';
  enabled: boolean;
  testMode: boolean;
  config: ShipStationConfig;
  credentials: ShipStationCredentials | null;
  providers: IntegrationProviders;
}

/** PUT /integrations/shipstation — partial update. */
export interface ShipStationIntegrationUpdateRequest {
  enabled?: boolean;
  testMode?: boolean;
  config?: Partial<ShipStationConfig>;
  credentials?: ShipStationCredentials | null;
  providers?: Partial<IntegrationProviders>;
}

/** POST /integrations/shipstation/test */
export interface ShipStationTestResponse {
  ok: boolean;
  message: string;
  productCountSample?: number;
  totalProducts?: number;
  /** Present when a V2 API key is stored; false if V2 probe was skipped. */
  v2Tested?: boolean;
  /** Present when `v2Tested` is true. */
  v2Ok?: boolean;
  /** Carrier count from V2 GET /carriers when V2 probe succeeds. */
  v2CarrierCount?: number;
}

/** GET /integrations/cartrover — redacted credentials on read. */
export interface CartRoverIntegrationResponse {
  id: string;
  storeId: string;
  provider: 'cartrover';
  enabled: boolean;
  testMode: boolean;
  config: CartRoverConfig;
  credentials: CartRoverCredentials | null;
  providers: IntegrationProviders;
}

/** PUT /integrations/cartrover — partial update. */
export interface CartRoverIntegrationUpdateRequest {
  enabled?: boolean;
  testMode?: boolean;
  config?: Partial<CartRoverConfig>;
  credentials?: Partial<CartRoverCredentials> | null;
  providers?: Partial<IntegrationProviders>;
}

/** POST /integrations/cartrover/test */
export interface CartRoverTestResponse {
  ok: boolean;
  message: string;
  inventorySampleCount?: number;
}

/** POST /integrations/cartrover/sync */
export interface CartRoverInventorySyncSummary {
  totalSynced: number;
  updatedCount: number;
  offset: number;
  totalAvailable: number;
  hasMore: boolean;
}

export type CartRoverResolutionSource =
  | 'override'
  | 'merchant_sku'
  | 'shipstation_meta'
  | 'none';

/** One storefront variant's resolved CartRover inventory SKU. */
export interface CartRoverMapping {
  variantId: string;
  productId: string;
  productName: string;
  variantName: string;
  merchantSku?: string;
  shipstationSku?: string;
  cartroverSku: string;
  cartroverOverride?: string;
  resolutionSource: CartRoverResolutionSource;
  inventoryQuantity: number;
  matchedInCartRover?: boolean;
}

export interface CartRoverUnmappedInventorySku {
  sku: string;
  quantity: number;
}

export interface CartRoverMappingsListResponse {
  mappings: CartRoverMapping[];
  total: number;
  limit: number;
  offset: number;
  unmappedInventorySkus?: CartRoverUnmappedInventorySku[];
  unmappedInventoryTotal?: number;
}

export interface CartRoverMappingsQuery {
  search?: string;
  productId?: string;
  limit?: number;
  offset?: number;
  includeUnmappedInventory?: boolean;
}

export interface CartRoverMappingUpdate {
  variantId: string;
  /** Explicit CartRover SKU override; null clears override (falls back to merchant / SS SKU). */
  cartroverSku: string | null;
}

export interface CartRoverMappingsUpdateRequest {
  mappings: CartRoverMappingUpdate[];
}

export const REDACTED_CREDENTIAL_VALUE = '••••••••';

/** True when API returned a redacted credential placeholder. */
export function isRedactedCredential(value: string | undefined): boolean {
  if (!value) {
    return false;
  }
  return value === REDACTED_CREDENTIAL_VALUE || value.startsWith('••');
}
